<template>
  <div class="oscillography-diagnostic-limits">
    <b-row class="oscillography-diagnostic-limits__filters">
      <b-col
        cols="12"
        lg="5"
      >
        <b-form-group
          :label="$t('engineering.oscillography-analysis.filters.maneuver-types')"
          label-for="oscillography-diagnostic-limits-maneuver-type"
        >
          <b-form-select
            id="oscillography-diagnostic-limits-maneuver-type"
            v-model="selectedManeuverType"
            :options="maneuverTypeOptions"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-table-simple
      ref="limitsTable"
      responsive
      sticky-header="38rem"
      striped
      hover
    >
      <b-thead>
        <b-tr>
          <b-th>
            {{ $t('entities.variables') }}
          </b-th>
          <b-th
            colspan="2"
            class="oscillography-diagnostic-limits__customization-heading"
          >
            <div class="oscillography-diagnostic-limits__customization-title">
              <span>{{ $t('general.customization') }}</span>
              <info-badge
                :string-key="parametersInfoTooltip"
              />
            </div>
            <b-dropdown
              v-if="canEdit"
              class="oscillography-diagnostic-limits__menu"
              variant="link"
              no-caret
              right
            >
              <template #button-content>
                <i
                  class="fas fa-ellipsis-v text-gray"
                  aria-hidden="true"
                />
              </template>
              <b-dropdown-item @click="$emit('restore')">
                {{ $t('actions.restore') }}
              </b-dropdown-item>
            </b-dropdown>
          </b-th>
        </b-tr>
        <b-tr>
          <b-th />
          <b-th>
            {{ $t('engineering.oscillography-analysis.diagnostic-parameters.lower-limit') }}
            <span
              class="text-danger"
              aria-hidden="true"
            >*</span>
          </b-th>
          <b-th>
            {{ $t('engineering.oscillography-analysis.diagnostic-parameters.upper-limit') }}
          </b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr
          v-for="limit in paginatedLimits"
          :key="limitKey(limit)"
        >
          <b-td>
            {{ variableLabel(limit.variable) }}
          </b-td>
          <b-td>
            <s-input-label-edit
              v-model="limit.lowerLimit"
              :editable="editMode"
              :formatted-value="formatNumberValue(limit.lowerLimit)"
            >
              <s-number-input
                v-model="limit.lowerLimit"
                :invalid="isLimitInvalid(limit)"
                :numeric-options="numericOptions"
              />
            </s-input-label-edit>
          </b-td>
          <b-td>
            <s-input-label-edit
              v-model="limit.upperLimit"
              :editable="editMode"
              :formatted-value="formatNumberValue(limit.upperLimit)"
            >
              <s-number-input
                v-model="limit.upperLimit"
                :invalid="isLimitInvalid(limit)"
                :numeric-options="numericOptions"
              />
            </s-input-label-edit>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <grid-paginator
      v-model="currentPage"
      :per-page="perPage"
      :total-rows="validLimits.length"
      class="oscillography-diagnostic-limits__paginator"
    />
  </div>
</template>

<script>
import GridPaginator from '@/components/lists/Paginator';
import InfoBadge from '@/components/icon/InfoBadge';
import { diagnosticManeuverType } from '@/domain/enums/installation/engineering/oscillographyAnalysis/oscillographyAnalysis';

const maneuverTypeNames = {
  opening: 'opening',
  closing: 'closing',
  motor: 'motor',
  [diagnosticManeuverType.OPENING]: 'opening',
  [diagnosticManeuverType.CLOSING]: 'closing',
  [diagnosticManeuverType.MOTOR]: 'motor',
};

export default {
  name: 'OscillographyAnalysisParametersDiagnosticLimitsTable',
  components: {
    GridPaginator,
    InfoBadge,
  },
  props: {
    limits: {
      type: Array,
      default: () => [],
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    invalidLimitKeys: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    currentPage: 1,
    perPage: 10,
    selectedManeuverType: 'opening',
  }),
  watch: {
    selectedManeuverType() {
      this.currentPage = 1;
      this.scrollTableToTop();
    },
    limits() {
      this.currentPage = 1;
    },
  },
  computed: {
    parametersInfoTooltip() {
      return this.$t('engineering.oscillography-analysis.diagnostic-parameters.parameters-info');
    },
    maneuverTypeOptions() {
      return ['opening', 'closing', 'motor'].map(maneuverType => ({
        text: this.maneuverTypeLabel(maneuverType),
        value: maneuverType,
      }));
    },
    validLimits() {
      return this.limits
        .filter(limit => (
          limit.variable
          && maneuverTypeNames[limit.maneuverType] === this.selectedManeuverType
        ))
        .sort((first, second) => (
          this.variableLabel(first.variable).localeCompare(
            this.variableLabel(second.variable),
            undefined,
            { sensitivity: 'base' },
          )
        ));
    },
    paginatedLimits() {
      const firstItemIndex = (this.currentPage - 1) * this.perPage;
      return this.validLimits.slice(firstItemIndex, firstItemIndex + this.perPage);
    },
    numericOptions() {
      return {
        decimalPlaces: 1,
        maximumValue: 1000000.0,
      };
    },
  },
  methods: {
    isLimitInvalid(limit) {
      return this.invalidLimitKeys.includes(this.limitKey(limit));
    },
    scrollTableToTop() {
      this.$refs.limitsTable.$el.scrollTop = 0;
    },
    limitKey(limit) {
      return `${limit.variable}-${limit.maneuverType}`;
    },
    variableLabel(variable) {
      return this.$t(`engineering.oscillography-analysis.variables.${variable}`);
    },
    maneuverTypeLabel(maneuverType) {
      const optionsKey = 'engineering.oscillography-analysis.filters.maneuver-type-options';
      const maneuverTypeName = maneuverTypeNames[maneuverType];

      return maneuverTypeName ? this.$t(`${optionsKey}.${maneuverTypeName}`) : '';
    },
    formatNumberValue(value, minimumFractionDigits = 1, maximumFractionDigits = 1) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, { minimumFractionDigits, maximumFractionDigits });
    },
  },
};
</script>

<style lang="sass" scoped>
.oscillography-diagnostic-limits
  &__filters
    margin-bottom: 1rem
  ::v-deep table
    margin-bottom: 0
    border: 0.0625rem solid var(--color-border-regular)
    border-radius: 0.3125rem
    border-collapse: separate
    border-spacing: 0
  ::v-deep th
    border: none
    vertical-align: middle
  ::v-deep td
    border: none
    vertical-align: middle
  ::v-deep thead th
    font-weight: bold
    text-align: center !important
    background-color: var(--color-table-body-bg)
  ::v-deep thead tr:last-child th
    border-bottom: 0.0625rem solid var(--color-border-regular)
  ::v-deep .b-table-sticky-header > .table.b-table > thead > tr:first-child > th
    z-index: 4
  ::v-deep .b-table-sticky-header > .table.b-table > thead > tr:nth-child(2) > th
    top: 3rem
    z-index: 3
  &__customization-heading
    position: relative
    background-color: var(--color-table-header-bg) !important
  &__customization-title
    display: flex
    align-items: center
    justify-content: center
    gap: .25rem
  &__menu
    position: absolute
    top: 50%
    right: .5rem
    transform: translateY(-50%)
  ::v-deep tbody td
    border-bottom: 0.0625rem solid var(--color-border-regular)
  ::v-deep tbody tr:last-child td
    border-bottom: 0
  &__paginator
    padding-top: 1rem
</style>
